import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  Typography,
  Zoom,
} from '@mui/material';
import { object, reach, string } from 'yup';

import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import PasswordStrengthBar from 'react-password-strength-bar';

import { ClearRounded } from '@mui/icons-material/';

import { userService } from '../services/login';

import EditIcon from '../shared/assets/icons/edit.svg?react';

import { useActions } from '../hooks/useActions';

import { classNames } from '../utils';
import { showToast } from '../utils/toast';

import { TextFieldUi } from '../components/UI/TextField/TextField';
import { ButtonUi } from '../components/UI/Button/Button';
import { dashboardStyle } from './style';
import { theme } from '../theme';

const defaultAccountData = {
  email: '',
  password: '',
  oldPassword: '',
  newPassword: '',
  passwordRepeat: '',
};

export const AccountManagementPage = (props) => {
  const { accountManagement } = dashboardStyle;
  const { t } = useTranslation();

  const {
    user,
    foundUser,
    isLoading = false,
  } = useSelector((state) => state.authentication);
  const { id: userId, email = '', username } = foundUser || {};

  const { changePassword, updateEmail, login } = useActions();

  const [accountData, setAccountData] = useState(defaultAccountData);
  const [validationErrors, setValidationErrors] = useState({});

  const [isEmailChanging, setIsEmailChanging] = useState(false);
  const [isPasswordChanging, setIsPasswordChanging] = useState(false);

  const changeEmailBtnIcon = !isEmailChanging ? (
    <EditIcon className='edit' />
  ) : (
    <ClearRounded />
  );
  const changePasswordBtnIcon = !isPasswordChanging ? (
    <EditIcon className='edit' />
  ) : (
    <ClearRounded />
  );

  const isSsoUser = user?.type === 'sso';
  const errorsExist = Object.keys(validationErrors).length > 0;

  const showEmail = !isSsoUser && !isPasswordChanging;
  const showPassword = !isEmailChanging;
  const showSubmitButton = isEmailChanging || isPasswordChanging;

  const enabledSubmitButton = isPasswordChanging
    ? accountData?.oldPassword &&
      accountData?.newPassword &&
      accountData?.passwordRepeat &&
      !errorsExist
    : accountData?.email && accountData?.password && !errorsExist;

  const submitButtonText = useMemo(() => {
    if (isLoading) return <CircularProgress />;
    if (isEmailChanging) return t('buttons.change-email');
    else return t('buttons.change-password');
  }, [isEmailChanging, isLoading, t]);

  /**
   * Get Validation schema for the form fields
   */
  const validationSchema = useMemo(
    () =>
      object().shape({
        email: !isSsoUser
          ? string().email(t('errors.validation.email')).notRequired()
          : string().notRequired(),
        password: isEmailChanging
          ? string().required(t('errors.validation.required'))
          : string().notRequired(),
        oldPassword: isPasswordChanging
          ? string().required(t('errors.validation.required'))
          : string().notRequired(),
        newPassword: string().when(
          'oldPassword',
          ([oldPassword], schema, options) => {
            const oldPasswordValue = accountData?.oldPassword || '';

            if (oldPasswordValue != '') {
              return (
                schema
                  .required(t('errors.validation.required'))
                  .min(10, t('password-reset.too-short'))
                  .matches(/[A-Z]/, t('password-reset.uppercase-letter'))
                  .matches(/[0-9]/, t('password-reset.number'))
                  .matches(/[^A-Za-z0-9]/, t('password-reset.symbol'))
                  .test(
                    'new-password-not-old',
                    t('password-reset.new-password-not-old'),
                    (value) => value !== oldPasswordValue,
                  )
              );
            } else {
              return schema.notRequired();
            }
          },
        ),
        passwordRepeat: string().when(
          'newPassword',
          ([newPassword], schema, options) => {
            const newPasswordValue = accountData?.newPassword;
            const passwordRepeatValue = options.value;
            if (newPasswordValue != '') {
              if (passwordRepeatValue == '')
                return schema.required(t('errors.validation.required'));
              if (newPasswordValue !== passwordRepeatValue)
                return schema.oneOf(
                  ['newPassword'],
                  t('errors.validation.password_match'),
                );
            } else {
              return schema;
            }
          },
        ),
      }),
    [
      accountData?.newPassword,
      accountData?.oldPassword,
      isEmailChanging,
      isPasswordChanging,
      isSsoUser,
      t,
    ],
  );

  /**
   * Validates form fields and sets error
   * @control field control name
   * @value field value
   */
  const validateControl = useCallback(
    async (control, value) => {
      const checkValue = value;

      try {
        const result = await reach(validationSchema, control).validate(
          checkValue,
          {
            abortEarly: false,
          },
        );
        setValidationErrors((prev) => {
          const { [control]: validControl, ...rest } = prev;
          return rest;
        });
      } catch (error) {
        const { message } = error?.inner?.[0] || {};
        setValidationErrors((prev) => ({
          ...prev,
          [control]: message,
        }));
      }
    },
    [validationSchema],
  );

  /**
   * Sets value to the textfield
   * @param event native event
   */
  const onChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setAccountData((current) => ({ ...current, [name]: value }));
      validateControl(name, value);
    },
    [validateControl],
  );

  /**
   * Shows form control field error message
   * @returns boolean value is form valid
   */
  const validateSubmitiion = async () => {
    let valid = true;

    Object.keys(accountData).forEach((control) => {
      validateControl(control, accountData[control]);
    });

    try {
      await validationSchema.validate(accountData, { abortEarly: false });
      valid = true;
    } catch (error) {
      valid = false;
    }

    return valid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const valid = await validateSubmitiion();
    if (!valid) return;

    const { email: newEmail, password, oldPassword, newPassword } = accountData;
    const emailData = { userId: user.userId, email };
    const passwordData = { oldPassword, newPassword };

    if (isEmailChanging)
      try {
        if (email === username)
          await userService.updateUser({ userId, username: newEmail });
        await updateEmail({ email, newEmail, password });
        await login({ username: newEmail, password });
        showToast(t('account-management.success-email'), 'success');
      } catch (exception) {
        console.error(exception);
      }

    if (isPasswordChanging)
      try {
        await changePassword(passwordData);
        showToast(t('account-management.success'), 'success');
        setIsPasswordChanging(false);
        setAccountData({ ...defaultAccountData, email });
      } catch (exception) {
        const exceptionCode = exception?.response?.data?.error?.code;
        if (exceptionCode)
          showToast(t(exceptionCode), 'error');
        else showToast(t('account-management.wrong-password'), 'error');
      }
  };

  /**
   * Shows form control field error message
   * @control field control name
   */
  const showValidationErrors = useCallback(
    (control) => {
      if (validationErrors[control])
        return (
          <FormHelperText key={control} error>
            {validationErrors?.[control]}
          </FormHelperText>
        );
      return null;
    },
    [validationErrors],
  );

  useEffect(() => {
    setAccountData((current) => ({ ...current, email }));
  }, [email]);

  return (
    <Box sx={accountManagement} className='organizations-wrapper'>
      <Box className='form-wrapper'>
        <Typography className='form-title'>
          {t('account-management.title')}
        </Typography>

        <Box component='form' className='validate-form' onSubmit={handleSubmit}>
          {showEmail && (
            <Box className='input-field'>
              <FormControl>
                <TextFieldUi
                  className='simple-text-field'
                  label={t('forms.email')}
                  name='email'
                  type='email'
                  value={accountData?.email || ''}
                  handleChange={onChange}
                  InputProps={{
                    readOnly: !isEmailChanging,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <ButtonUi
                          isIconBtn
                          className='input-btn'
                          size='small'
                          tooltipTitle={
                            !isEmailChanging ? t('buttons.change-email') : ''
                          }
                          onClickHandler={() =>
                            setIsEmailChanging((prev) => !prev)
                          }
                        >
                          {changeEmailBtnIcon}
                        </ButtonUi>
                      </InputAdornment>
                    ),
                  }}
                />
                {showValidationErrors('email')}
              </FormControl>
            </Box>
          )}
          {isEmailChanging && (
            <Box className='input-field'>
              <FormControl>
                <TextFieldUi
                  className='simple-text-field'
                  label={t('forms.password')}
                  name='password'
                  type='password'
                  value={accountData?.password || ''}
                  handleChange={onChange}
                />
                {showValidationErrors('password')}
              </FormControl>
            </Box>
          )}
          {showPassword && (
            <Box className='input-field'>
              <FormControl>
                <TextFieldUi
                  className='simple-text-field'
                  label={
                    !isPasswordChanging
                      ? t('forms.password')
                      : t('forms.old-password')
                  }
                  name={!isPasswordChanging ? 'password' : 'oldPassword'}
                  type='password'
                  value={
                    !isPasswordChanging
                      ? 'password'
                      : accountData?.oldPassword || ''
                  }
                  handleChange={onChange}
                  InputProps={{
                    readOnly: !isPasswordChanging,
                    endAdornment: (
                      <InputAdornment position='end'>
                        <ButtonUi
                          isIconBtn
                          className='input-btn'
                          size='small'
                          tooltipTitle={
                            !isPasswordChanging
                              ? t('buttons.change-password')
                              : ''
                          }
                          onClickHandler={() =>
                            setIsPasswordChanging((prev) => !prev)
                          }
                        >
                          {changePasswordBtnIcon}
                        </ButtonUi>
                      </InputAdornment>
                    ),
                  }}
                />
                {showValidationErrors(
                  !isPasswordChanging ? 'password' : 'oldPassword',
                )}
              </FormControl>
            </Box>
          )}
          {isPasswordChanging && (
            <>
              <Box className='input-field'>
                <FormControl>
                  <TextFieldUi
                    className='simple-text-field'
                    label={t('forms.new-password')}
                    name='newPassword'
                    type='password'
                    value={accountData?.newPassword || ''}
                    handleChange={onChange}
                  />
                  <Box className='password-strength-bar-container'>
                    <PasswordStrengthBar
                      className='strength-bar'
                      scoreWords={[]}
                      shortScoreWord={null}
                      barColors={[
                        theme?.palette.neutral.grey30,
                        theme?.palette.neutral.error,
                        theme?.palette.dashBoar.softPeach,
                        theme?.palette.myPrimary.lightBlue70,
                        theme?.palette.dashBoar.mintGreen,
                      ]}
                      password={accountData?.newPassword}
                    />
                  </Box>
                  {showValidationErrors('newPassword')}
                </FormControl>
              </Box>
              <Box className='input-field'>
                <FormControl>
                  <TextFieldUi
                    className='simple-text-field'
                    label={t('forms.validate-new-password')}
                    name='passwordRepeat'
                    type='password'
                    value={accountData?.passwordRepeat || ''}
                    handleChange={onChange}
                  />
                  {showValidationErrors('passwordRepeat')}
                </FormControl>
              </Box>
            </>
          )}

          <Zoom
            in={showSubmitButton}
            timeout={{ enter: 300, exit: 0 }}
            mountOnEnter
            unmountOnExit
          >
            <Box>
              <ButtonUi
                className={classNames('accept', { loading: isLoading }, [
                  'action-btn',
                ])}
                variant='contained'
                type='primary'
                size='small'
                btnType='submit'
                disabled={!enabledSubmitButton}
              >
                {submitButtonText}
              </ButtonUi>
            </Box>
          </Zoom>
        </Box>
      </Box>
    </Box>
  );
};
